import { forwardsApi } from "@/forwards2/forwardsApi";
import {
  DEAL_STATUS_FILTERS,
  DEFAULT_DEAL_STATUS_FILTERS,
  DEFAULT_TRANSACTION_STATUS_FILTERS,
  TRANSACTION_STATUSES,
  type DealRow,
  type DealStatus,
  type DealsQuery,
  type ForwardYearTotal,
  type OverviewResponse,
  type TransactionStatus,
  type UpcomingDeliveriesQuery,
  type UpcomingDeliveryRow,
} from "@/forwards2/forwardsTypes";
import { useQueryManager } from "@/composables/useQueryManager";
import { addMonths, format, isValid, parseISO, subMonths } from "date-fns";
import { defineStore } from "pinia";
import { computed, readonly, ref, shallowRef, watch } from "vue";

/** How far ahead the upcoming-deliveries card looks until the user picks a window of their own. */
export const DEFAULT_DELIVERY_WINDOW_MONTHS = 3;

const DEFAULT_DELIVERY_WINDOW_LOOKBACK_MONTHS = 2;

const ISO_DATE = "yyyy-MM-dd";
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The upcoming-deliveries card's window-end range until the user picks one: two months ago to three months on.
 *
 * Read on every access rather than once, so a page left open past midnight moves with the calendar.
 */
export function defaultDeliveryWindow(): { from: string; to: string } {
  const today = new Date();
  return {
    from: format(
      subMonths(today, DEFAULT_DELIVERY_WINDOW_LOOKBACK_MONTHS),
      ISO_DATE
    ),
    to: format(addMonths(today, DEFAULT_DELIVERY_WINDOW_MONTHS), ISO_DATE),
  };
}

/**
 * The forwards overview page's state: the four requests behind it, and the filters that drive them.
 *
 * The deliveries and deals filters live in the URL so the back button and a pasted link both work.
 * Multi-valued filters are comma-joined scalars there (`?status=draft,confirmed`) because
 * `useQueryManager` models one value per key; the request still sends the repeated `status[]`
 * parameters the endpoints expect. The deliveries' keys carry a `deliveries_` prefix so they never
 * collide with the deals table's.
 *
 * A delivery window end missing from the URL is the default range; present but empty, it is an
 * open end the user chose by clearing the picker.
 *
 * The requests are independent — each card keeps working when another fails — so each carries its
 * own loading and error state.
 */
export const useOverviewStore = defineStore("forwards2Overview", () => {
  const { getQueryValue, parsers, updateQuery } = useQueryManager();

  const deliveriesStatusParam = getQueryValue(
    "deliveries_status",
    parsers.stringWithDefault(DEFAULT_TRANSACTION_STATUS_FILTERS.join(","))
  );
  const deliveriesFromParam = getQueryValue("deliveries_from", parsers.string);
  const deliveriesToParam = getQueryValue("deliveries_to", parsers.string);
  const deliveriesDealParam = getQueryValue(
    "deliveries_deal",
    parsers.stringWithDefault("")
  );

  const statusParam = getQueryValue(
    "status",
    parsers.stringWithDefault(DEFAULT_DEAL_STATUS_FILTERS.join(","))
  );
  const nameParam = getQueryValue("name", parsers.stringWithDefault(""));
  const mandateParam = getQueryValue("mandate", parsers.stringWithDefault(""));
  const companyParam = getQueryValue("company", parsers.stringWithDefault(""));

  const overview = ref<OverviewResponse | null>(null);
  const overviewLoading = ref(false);
  // `shallowRef`: keeps foreign error objects out of a reactive proxy (identity, `instanceof`; C-09b-11).
  const overviewError = shallowRef<unknown>(null);

  const deliveries = ref<UpcomingDeliveryRow[] | null>(null);
  const deliveriesLoading = ref(false);
  const deliveriesError = shallowRef<unknown>(null);

  const deals = ref<DealRow[] | null>(null);
  const dealsLoading = ref(false);
  const dealsError = shallowRef<unknown>(null);

  const yearTotals = ref<ForwardYearTotal[] | null>(null);
  const yearTotalsLoading = ref(false);
  const yearTotalsError = shallowRef<unknown>(null);

  const deliveryFilters = computed<UpcomingDeliveriesQuery>({
    get: () => {
      const defaults = defaultDeliveryWindow();
      return {
        transactionStatus: parseStatuses(
          deliveriesStatusParam.value,
          TRANSACTION_STATUSES,
          DEFAULT_TRANSACTION_STATUS_FILTERS
        ),
        windowEndFrom: parseWindowEnd(deliveriesFromParam.value, defaults.from),
        windowEndTo: parseWindowEnd(deliveriesToParam.value, defaults.to),
        dealName: deliveriesDealParam.value,
      };
    },
    // One `updateQuery` for every key — see `dealFilters`.
    set: (value) => {
      const defaults = defaultDeliveryWindow();
      const statuses =
        value.transactionStatus.length === 0
          ? TRANSACTION_STATUSES
          : value.transactionStatus;
      void updateQuery({
        deliveries_status: statuses.join(","),
        deliveries_from: windowEndParam(value.windowEndFrom, defaults.from),
        deliveries_to: windowEndParam(value.windowEndTo, defaults.to),
        deliveries_deal: blankToUndefined(value.dealName),
      });
    },
  });

  const dealFilters = computed<DealsQuery>({
    get: () => ({
      status: parseStatuses(
        statusParam.value,
        DEAL_STATUS_FILTERS,
        DEFAULT_DEAL_STATUS_FILTERS
      ),
      name: nameParam.value,
      mandate: mandateParam.value,
      company: companyParam.value,
    }),
    // One `updateQuery` for all four keys, not four writes: each write builds its new query from
    // the route as it is *now*, and the route only catches up once the push has resolved — so four
    // writes in a tick would each start from the same stale query and only the last would survive.
    set: (value) => {
      // An emptied selector means every status, which the selector labels "All statuses"; left
      // blank it would fall back to the default pair on the way back in.
      const statuses =
        value.status.length === 0 ? DEAL_STATUS_FILTERS : value.status;
      void updateQuery({
        status: statuses.join(","),
        name: blankToUndefined(value.name),
        mandate: blankToUndefined(value.mandate),
        company: blankToUndefined(value.company),
      });
    },
  });

  const attentionRows = computed(() => overview.value?.needs_attention ?? []);
  const upcomingDeliveries = computed(() => deliveries.value ?? []);
  const dealRows = computed(() => deals.value ?? []);
  const yearTotalRows = computed(() => yearTotals.value ?? []);

  // Each URL change fires a new request while older ones may still be in flight, so every loader
  // stamps its request and only the newest may write — an older answer arriving late would
  // otherwise replace the rows and clear the loading flag for filters the user has already left.
  let overviewGeneration = 0;
  let deliveriesGeneration = 0;
  let dealsGeneration = 0;
  let yearTotalsGeneration = 0;

  /** Fetches the attention rows. Never throws — read `overviewError`. */
  const loadOverview = async () => {
    const generation = ++overviewGeneration;
    overviewLoading.value = true;
    try {
      const response = await forwardsApi.getOverview();
      if (generation !== overviewGeneration) return;
      overview.value = response.data;
      overviewError.value = null;
    } catch (caught) {
      if (generation !== overviewGeneration) return;
      overviewError.value = caught;
      overview.value = null;
    } finally {
      if (generation === overviewGeneration) overviewLoading.value = false;
    }
  };

  /** Fetches the upcoming deliveries for the current filters. Never throws — read `deliveriesError`. */
  const loadDeliveries = async () => {
    const generation = ++deliveriesGeneration;
    deliveriesLoading.value = true;
    try {
      const response = await forwardsApi.listUpcomingDeliveries(
        deliveryFilters.value
      );
      if (generation !== deliveriesGeneration) return;
      deliveries.value = response.data.rows;
      deliveriesError.value = null;
    } catch (caught) {
      if (generation !== deliveriesGeneration) return;
      deliveriesError.value = caught;
      deliveries.value = null;
    } finally {
      if (generation === deliveriesGeneration) deliveriesLoading.value = false;
    }
  };

  /** Fetches the managed deals for the current filters. Never throws — read `dealsError`. */
  const loadDeals = async () => {
    const generation = ++dealsGeneration;
    dealsLoading.value = true;
    try {
      const response = await forwardsApi.listDeals(dealFilters.value);
      if (generation !== dealsGeneration) return;
      deals.value = response.data.rows;
      dealsError.value = null;
    } catch (caught) {
      if (generation !== dealsGeneration) return;
      dealsError.value = caught;
      deals.value = null;
    } finally {
      if (generation === dealsGeneration) dealsLoading.value = false;
    }
  };

  /** Fetches the per-year forward totals. Never throws — read `yearTotalsError`. */
  const loadYearTotals = async () => {
    const generation = ++yearTotalsGeneration;
    yearTotalsLoading.value = true;
    try {
      const response = await forwardsApi.getYearTotals();
      if (generation !== yearTotalsGeneration) return;
      yearTotals.value = response.data.years;
      yearTotalsError.value = null;
    } catch (caught) {
      if (generation !== yearTotalsGeneration) return;
      yearTotalsError.value = caught;
      yearTotals.value = null;
    } finally {
      if (generation === yearTotalsGeneration) yearTotalsLoading.value = false;
    }
  };

  const load = async () => {
    await Promise.all([
      loadOverview(),
      loadDeliveries(),
      loadDeals(),
      loadYearTotals(),
    ]);
  };

  watch(
    [
      deliveriesStatusParam,
      deliveriesFromParam,
      deliveriesToParam,
      deliveriesDealParam,
    ],
    () => {
      void loadDeliveries();
    }
  );

  watch([statusParam, nameParam, mandateParam, companyParam], () => {
    void loadDeals();
  });

  return {
    deliveryFilters,
    dealFilters,
    attentionRows,
    upcomingDeliveries,
    dealRows,
    yearTotalRows,
    overview: readonly(overview),
    overviewLoading: readonly(overviewLoading),
    deliveriesLoading: readonly(deliveriesLoading),
    dealsLoading: readonly(dealsLoading),
    yearTotalsLoading: readonly(yearTotalsLoading),
    // Computeds rather than `readonly()`: the values are foreign error objects and a readonly proxy
    // of one would break identity checks and `instanceof` for no benefit.
    overviewError: computed(() => overviewError.value),
    deliveriesError: computed(() => deliveriesError.value),
    dealsError: computed(() => dealsError.value),
    yearTotalsError: computed(() => yearTotalsError.value),
    load,
    loadOverview,
    loadDeliveries,
    loadDeals,
    loadYearTotals,
  };
});

/**
 * Reads a comma-joined status filter back out of the URL.
 *
 * Unknown values are dropped, and an empty result falls back to the default — which is also what
 * the endpoints apply when the parameter is missing, so the two agree.
 */
function parseStatuses<T extends DealStatus | TransactionStatus>(
  value: string,
  known: readonly T[],
  fallback: readonly T[]
): T[] {
  const parsed = value
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is T => (known as readonly string[]).includes(part));
  return parsed.length > 0 ? parsed : [...fallback];
}

/** Missing or unreadable reads as the default (a hand-edited URL must not become a 400); empty is an open end. */
function parseWindowEnd(
  value: string | undefined,
  fallback: string
): string | null {
  if (value === undefined) return fallback;
  if (value === "") return null;
  return ISO_DATE_PATTERN.test(value) && isValid(parseISO(value))
    ? value
    : fallback;
}

/** The default stays out of the URL, so a bare link keeps following the calendar; an open end is kept as empty. */
function windowEndParam(
  value: string | null,
  fallback: string
): string | undefined {
  if (value === fallback) return undefined;
  return value ?? "";
}

/** An empty filter is absent from the URL rather than present and blank. */
function blankToUndefined(value: string): string | undefined {
  return value === "" ? undefined : value;
}
